import clsx from "clsx";
import { useState, useEffect, useContext } from "react";
import { DatabaseRecordInfo } from "lib/types";
import {
  deleteRecord,
  getDatabasesRecord,
} from "pages/dashboard/database/service";
import moment from "moment";
import { ALERT_VEHICLE_CAR_COLOR } from "lib/constants";
import { SvgRemove, SvgEdit, SvgAdd, SvgPlateRecord } from "ui/svgs";
import { useSearchParams, useParams } from "react-router-dom";
import { showToast } from "ui/components/common";
import { Skeleton, Popconfirm, Tooltip } from "antd";
import Search from "ui/search";
import RecordModal from "ui/database/record-modal";
import { DatabaseContext } from "context/DatabaseContext";

export default function DatabaseContent() {
  const { databaseid } = useParams();
  const { queryDatabases } = useContext(DatabaseContext);
  const [searchParams] = useSearchParams();

  const tag = searchParams.get("tag") || "";

  const [records, setRecords] = useState<DatabaseRecordInfo[]>([]);
  const [curRecord, setCurRecord] = useState<DatabaseRecordInfo>();

  const [isLoading, setIsLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    if (!databaseid) {
      return;
    }
    setRecords([]);
    queryDatabasesRecord(tag);
    return () => {
      setRecords([]);
    };
  }, [tag, databaseid]);

  const queryDatabasesRecord = async (description?: string) => {
    try {
      setIsLoading(true);
      const response = await getDatabasesRecord(databaseid, description);
      setIsLoading(false);
      if (response.status !== 200) {
        showToast(1, "There was a problem fetching records. Please try again.");
        return;
      }
      const data = response.data;
      setRecords(data?.records);
    } catch (error) {
      console.error(error);
    }
  };

  const handleDeleteRecord = async (id: string) => {
    const res = await deleteRecord(id);
    if (res.status !== 200) {
      showToast(
        1,
        "There was a problem deleting the record. Please try again.",
      );
      return;
    }
    showToast(0, "Record deleted successfully!");
    queryDatabasesRecord(tag);
    queryDatabases();
  };

  const handleEditRecord = (record: DatabaseRecordInfo) => {
    setModalOpen(true);
    setCurRecord(record);
  };

  const hanleAddRecord = () => {
    setModalOpen(true);
    setCurRecord(null);
  };

  const handleSave = () => {
    setModalOpen(false);
    queryDatabasesRecord(tag);
    queryDatabases();
  };

  return (
    <div className="pageContent w-full h-full flex flex-col ">
      <div className="w-full flex justify-between ">
        <Search
          placeholder="Search Description"
          className="relative flex flex-shrink-0 h-[46px] w-[290px]"
          query="tag"
          numberAndLetter={true}
        />
        <button
          className={clsx(
            "flex justify-center h-[46px] w-[132px] gap-2 items-center rounded-2xl bg-[#3E83F6] text-base font-medium text-white  hover:bg-blue-400",
          )}
          onClick={hanleAddRecord}
        >
          <SvgAdd className="fill-white " width="11" />
          Record
        </button>
      </div>
      <div className="overflow-y-auto mt-6 flex flex-wrap content-start gap-4">
        {records?.map((record) => (
          <div className="w-[432px] h-[180px] bg-[#FAFAFA] rounded-2xl p-4">
            <div className="flex gap-2">
              {record.type === "lpr" ? (
                <div className="w-[160px] h-[120px] rounded-xl bg-[#EEEEEE] flex items-center justify-center overflow-hidden flex-shrink-0">
                  <SvgPlateRecord width="72" className="fill-[#BDBDBD]" />
                </div>
              ) : (
                <div className="w-[160px] h-[120px] rounded-xl bg-black flex items-center justify-center overflow-hidden flex-shrink-0">
                  <img
                    src={record?.image?.url}
                    className="max-h-full max-w-full"
                  />
                </div>
              )}
              <div className="flex-grow px-1 h-full max-h-[120px] overflow-y-hidden ">
                <p className="break-normal font-semibold ">
                  {record?.name || "-"}
                </p>
                {record.type === "human" || record.type === "car" ? (
                  <Tooltip title={record?.description}>
                    <p className="break-normal overflow-hidden line-clamp-4 text-ellipsis">
                      {record?.description || "-"}
                    </p>
                  </Tooltip>
                ) : (
                  <>
                    {record?.lpr?.model && (
                      <p className="text-zinc-600">
                        Model:
                        <span className="ml-1 text-[#222222]">
                          {record?.lpr?.model}
                        </span>
                      </p>
                    )}
                    {record?.lpr?.color && (
                      <p className="flex items-center text-zinc-600">
                        Color:
                        <div
                          className="rounded-[4px] w-4 h-4 mr-2 ml-1"
                          style={{
                            backgroundColor:
                              ALERT_VEHICLE_CAR_COLOR[record?.lpr?.color],
                          }}
                        />
                        <span className="text-[#222222]">
                          {record?.lpr?.color}
                        </span>
                      </p>
                    )}
                    {record?.lpr?.year > 0 && (
                      <p className="text-zinc-600">
                        Year:
                        <span className="ml-1 text-[#222222]">
                          {record?.lpr?.year}
                        </span>
                      </p>
                    )}
                    {record?.lpr?.licensePlate && (
                      <p className="text-zinc-600">
                        LPR:
                        <span className="ml-1 text-[#222222]">
                          {record?.lpr?.licensePlate}
                        </span>
                      </p>
                    )}
                    {record?.lpr?.state && (
                      <p className="text-zinc-600">
                        State:
                        <span className="ml-1 text-[#222222]">
                          {record?.lpr?.state}
                        </span>
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>
            <div className="flex justify-between mt-2 ">
              <p className="text-[#717171] text-sm leading-5">
                Created by {moment(+record.createdAt).format("MMM DD, YYYY")}
              </p>
              <div className="flex-shrink-0">
                <button
                  className="rounded-full text-neutral-500 px-1 fill-neutral-500 hover:fill-neutral-400"
                  onClick={() => handleEditRecord(record)}
                >
                  <SvgEdit width="18" />
                </button>
                <Popconfirm
                  overlayClassName="deletePopConfirm"
                  title="Delete the record"
                  description="Are you sure to delete this record?"
                  onConfirm={() => handleDeleteRecord(record?.id)}
                  okText="Yes"
                  cancelText="No"
                  okButtonProps={{}}
                  icon={null}
                >
                  <button className="rounded-full text-neutral-500 px-1 fill-neutral-500 hover:fill-neutral-400">
                    <SvgRemove width="19" />
                  </button>
                </Popconfirm>
              </div>
            </div>
          </div>
        ))}
      </div>
      {!databaseid && (
        <div className="flex flex-col justify-center w-full flex-grow">
          <p className="text-lg  text-center font-semibold">
            Please select a database
          </p>
        </div>
      )}
      {records?.length === 0 && isLoading && (
        <div className="flex justify-center w-full h-full px-4 pt-4">
          <Skeleton />
        </div>
      )}

      <RecordModal
        editType={curRecord?.id ? "edit" : "add"}
        visible={modalOpen}
        record={curRecord}
        onClose={() => setModalOpen(false)}
        onSave={handleSave}
      />
    </div>
  );
}
